/**
 * Hook pour obtenir le wording dynamique selon le vendorType de la société
 *
 * Usage :
 * - useVendorWording(societyId) → wording basé sur la société spécifique
 * - useCurrentUserWording() → wording basé sur la société de l'utilisateur connecté
 */

import { useMemo } from 'react';
import { useSociety } from '../features/societies/hooks/useSocieties';
import { useAuthStore } from '../stores/auth-store';
import { getVendorWording, type VendorWording } from '../utils/vendorWording';

/**
 * Wording dynamique basé sur une société spécifique
 */
export function useVendorWording(societyId: string | undefined): VendorWording {
  const { isAuthenticated } = useAuthStore();
  const { data: society } = useSociety(societyId, { enabled: isAuthenticated });
  return useMemo(() => getVendorWording(society?.vendorType), [society?.vendorType]);
}

/**
 * Wording dynamique basé sur la société de l'utilisateur connecté
 * Utile pour les éléments globaux (Sidebar, pages de liste)
 */
export function useCurrentUserWording(): VendorWording {
  const { currentUser, isAuthenticated } = useAuthStore();
  const societyId = currentUser?.type === 'EXTERNAL' ? currentUser.societyId : undefined;
  const { data: society } = useSociety(societyId || '', { enabled: isAuthenticated });
  return useMemo(() => getVendorWording(society?.vendorType), [society?.vendorType]);
}
